import { Component, OnInit, OnDestroy, ComponentRef } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { Variables, VariableType } from '~/core/classes/user/variables';

import { GraphService } from '~/core/services/graph.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { HttpService } from '~/core/services/http.service';

import { AppSettings } from '~/core/models/appsettings';

// Remove hardcoded ALLOWED_EMAILS, will load from API
let ALLOWED_EMAILS: string[] = [];

export interface Modal {
    type?: ComponentRef<any>;
    inputs?: any;
    outputs?: any
}
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})

export class AppComponent implements OnInit, OnDestroy {
    private appSettings: AppSettings = new AppSettings();
    private readonly _destroying$ = new Subject<void>();

    public error: string = "";
    public title: string = "";
    public expand: boolean = false;

    public showNav: boolean = false;
    public showFooter: boolean = true;

    public layoutCssClass: string = "";

    public isLoggedIn: boolean = false;
    public isLoading: boolean = true;

    // Alert
    public showAlert: boolean = false;
    public alertTitle: string = "Alert";
    public alertMessage: string = "";
    public alertContinueText: string = "OK";
    public alertCancelText: string = "";

    // Modal
    public modal: Modal | null = null;

    constructor(
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private graphService: GraphService,
        private router: Router,
        private appSettingsService: AppSettingsService,
        private httpService: HttpService // Inject HttpService
    ) {
        window.onbeforeunload = null;
        sessionStorage.removeItem("__allowNavigate__");

        setTimeout(() => {
            if (this.isLoggedIn) {
                this.layoutCssClass = Variables.get("Nav.LayoutCssClass", VariableType.Local) ?? "";
                this.setLayoutClass(false);
            }
        }, 1500);
    }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public async ngOnInit() {
        await this.appSettingsService.getSettings(true).then(async (appSettings: AppSettings) => {
            if (appSettings) {
                this.appSettings = appSettings;
                this.title = this.appSettings.title;
                this.showNav = false; // Sidebar removed
                this.showFooter = this.appSettings.showFooter;

                this.getMSALAccounts();
            } else {
                this.error = "Unable to load application settings.";
                this.isLoading = false;
            }
        });
    }

    public ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public setLayoutClass(change: boolean = true) {
        if (change) {
            if (this.layoutCssClass !== "") {
                this.layoutCssClass = "";
            } else {
                this.layoutCssClass = "expand";
            }
        }

        if (this.layoutCssClass === "") {
            this.expand = false;
        } else {
            setTimeout(() => {
                this.expand = true;
            }, 100);
        }

        Variables.set("Nav.LayoutCssClass", this.layoutCssClass, VariableType.Local);
    }

    public async reloadPage(page = "") {
        let currentUrl = page === "" ? this.router.url : page;

        this.router.navigate([currentUrl]);
    }

    //*************************************************************************
    //  Alert Methods
    //*************************************************************************
    public alertContinue() {
        this.showAlert = false;
    }

    public alertCancel() {
        this.showAlert = false;
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************
    private getMSALAccounts() {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                let account: AccountInfo | null = this.authService.instance.getActiveAccount();

                if (account === null) {
                    account = this.getAccountInfo();
                }

                this.setAccountInfo(account);
            });
    }

    private getAccountInfo() {
        let accounts = this.authService.instance.getAllAccounts();
        let account: AccountInfo | null = null;

        if (accounts.length > 0) {
            this.authService.instance.setActiveAccount(accounts[0]);
            account = accounts[0];
        }

        return account;
    }

    private async setAccountInfo(account: AccountInfo | null) {
        let allowAccess: boolean = this.appSettings.openAccess;

        // Restrict access to only allowed emails loaded from API
        // if (account && account.username && !ALLOWED_EMAILS.includes(account.username.toLowerCase())) {
        //     allowAccess = false;
        // }

        let roles: any = [];

        // for (let role of this.appSettings.roles) {
        //     let groupAccess: any = await this.graphService.getUserGroups(role);

        //     if (groupAccess?.value.length > 0) {
        //         roles.push(role);
        //     }
        // }

        // if (!this.appSettings.openAccess && roles.length > 0) {
        //     allowAccess = true;
        // }

        if (account) {
            await this.setUserInfo(account, roles);
            await this.appSettingsService.saveSettings(this.appSettings);
        }

        if (allowAccess) {
            this.layoutCssClass = "";
            this.isLoggedIn = true;
        } else {
            this.layoutCssClass = "noaccess";
            this.isLoggedIn = false;
        }

        this.isLoading = false;
    }

    private async setUserInfo(account: any, roles: any) {
        account.photo = "";

        if (account.name) {
            const name: string[] = account.name.split(", ");

            if (name && name.length > 1) {
                const firstName: string = name[1].split(" (")[0].split(" [")[0];
                const lastName: string = name[0];

                account.firstName = firstName;
                account.lastName = lastName;
                account.name = `${firstName} ${lastName}`;
                account.initials = firstName.substring(0, 1).toUpperCase() + lastName.substring(0, 1).toUpperCase();
            }
        }

        account.roles = roles;

        this.appSettings.user = account;
    }
}
